import { Download, FileText, Paperclip, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Spinner } from "@/components/ui";
import { fileUrl, formatSize, type PendingFile } from "@/lib/files";
import { isLocal, localFiles, serverLocalUrl } from "@/lib/temp";

/**
 * Where to load an attachment from. Files of private temporary chats come from this browser
 * (IndexedDB); while one is not there yet (a tool just made it), from the server's memory.
 */
function useFileSrc(id: string): { src: string | null; download: string; local: boolean } {
  const local = isLocal(id);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (!local) return;
    let url: string | null = null;
    let alive = true;
    let tries = 0;
    const load = () =>
      localFiles.get(id).then((f) => {
        if (!alive) return;
        if (f) {
          url = URL.createObjectURL(f.blob);
          setBlobUrl(url);
        } else if (tries++ < 3) {
          // The event with the file may still be on its way.
          setTimeout(load, 500);
        } else setMissing(true);
      });
    void load();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, local]);
  if (!local) return { src: fileUrl(id), download: fileUrl(id, true), local };
  const src = blobUrl ?? (missing ? serverLocalUrl(id) : null);
  return { src, download: blobUrl ?? serverLocalUrl(id, true), local };
}

export function Lightbox({ src, alt, onClose, download }: { src: string; alt: string; onClose: () => void; download?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} role="dialog" aria-label={alt}>
      <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
      <div className="absolute top-4 right-4 flex gap-2">
        <a
          href={download ?? `${src}${src.includes("?") ? "&" : "?"}download=true`}
          download={download?.startsWith("blob:") ? alt : undefined}
          className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
          onClick={(e) => e.stopPropagation()}
          aria-label="Download"
        >
          <Download size={20} />
        </a>
        <button className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

/** An attachment inside a message: image thumbnail (click to enlarge) or a file chip. */
export function AttachmentView({ id, name, mime }: { id: string; name?: string; mime?: string }) {
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const label = name ?? "file";
  const file = useFileSrc(id);
  if (mime?.startsWith("image/") && !broken) {
    if (!file.src) return <span className="my-1 mr-2 inline-flex h-24 w-24 items-center justify-center rounded-lg border border-zinc-200 align-top dark:border-zinc-700"><Spinner size={16} /></span>;
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} className="my-1 mr-2 inline-block overflow-hidden rounded-lg border border-zinc-200 align-top dark:border-zinc-700">
          <img src={file.src} alt={label} loading="lazy" onError={() => setBroken(true)} className="max-h-64 max-w-full object-contain" />
        </button>
        {open && <Lightbox src={file.src} alt={label} download={file.download} onClose={() => setOpen(false)} />}
      </>
    );
  }
  return (
    <a
      href={file.download}
      download={file.download.startsWith("blob:") ? label : undefined}
      className="my-1 mr-2 inline-flex max-w-xs items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm align-top hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
      title={broken ? "File no longer available" : `Download ${label}`}
    >
      <FileText size={16} className="shrink-0 text-zinc-500" />
      <span className="truncate">{label}</span>
    </a>
  );
}

/** Pending uploads shown above the composer's text field. */
export function PendingFiles({ files, onRemove }: { files: PendingFile[]; onRemove: (key: string) => void }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 pt-3">
      {files.map((f) => (
        <div key={f.key} className="relative flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-1.5 pr-7 text-xs dark:border-zinc-700 dark:bg-zinc-800">
          {f.preview ? <img src={f.preview} alt="" className="h-10 w-10 rounded object-cover" /> : <FileText size={20} className="mx-1 text-zinc-500" />}
          <div className="max-w-40 min-w-0">
            <p className="truncate font-medium">{f.file.name}</p>
            <p className={f.error ? "truncate text-red-600" : "text-zinc-500"}>
              {f.error ?? (f.attachment ? formatSize(f.file.size) : `${Math.round(f.progress * 100)}%`)}
            </p>
          </div>
          {!f.attachment && !f.error && <Spinner size={12} />}
          <button type="button" onClick={() => onRemove(f.key)} className="absolute top-1 right-1 rounded p-0.5 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700" aria-label={`Remove ${f.file.name}`}>
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function AttachButton({ onFiles }: { onFiles: (files: FileList) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => input.current?.click()}
        className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        aria-label="Attach files"
        title="Attach files"
      >
        <Paperclip size={18} />
      </button>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}

/** Accepts files dropped anywhere on its area and shows an overlay while dragging. */
export function DropZone({ onFiles, children, className }: { onFiles: (files: FileList) => void; children: ReactNode; className?: string }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  return (
    <div
      className={`relative ${className ?? ""}`}
      onDragEnter={(e) => {
        if (!e.dataTransfer.types.includes("Files")) return;
        depth.current += 1;
        setOver(true);
      }}
      onDragLeave={() => {
        depth.current -= 1;
        if (depth.current <= 0) setOver(false);
      }}
      onDragOver={(e) => e.dataTransfer.types.includes("Files") && e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
    >
      {children}
      {over && (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-2xl border-2 border-dashed border-indigo-500 bg-indigo-50/80 text-sm font-medium text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300">
          Drop files to attach
        </div>
      )}
    </div>
  );
}
